/* Card receipts stay mounted for Northbound; native popovers paint above rails
 * without resizing cards. Delegation covers cards inserted by section renders. */
(() => {
  const selector = '.card__breakdown';
  const triggerSelector = '.card__breakdown-trigger';
  const panelSelector = '.card__breakdown-panel';
  const hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
  const states = new WeakMap();
  let active = null;
  let frame = null;
  let escapeKeyup = false;
  let hoverDismissed = false;
  let pointerPosition = null;
  let dismissedPosition = null;
  const asElement = (node) => node instanceof Element ? node : node?.parentElement;
  const cardFor = (node) => asElement(node)?.closest('.product-card-wrapper');
  const stateFor = (root) => {
    if (!states.has(root)) states.set(root, { pinned: false, hovered: false, suppressed: false, timer: null });
    return states.get(root);
  };
  const panelFor = (root) => root.querySelector(panelSelector);
  const triggerFor = (root) => root.querySelector(triggerSelector);
  const isReady = (root) => {
    const surface = root.querySelector('[data-northbound-price-surface="card-receipt"]');
    return Boolean(root.isConnected && surface?.hasAttribute('data-northbound-shipping-cents') &&
      surface.hasAttribute('data-northbound-grand-total-cents') &&
      surface.querySelector('[data-card-receipt-shipping-before-subsidy]')?.textContent.trim() &&
      surface.querySelector('[data-northbound-grand-total-display]')?.textContent.trim());
  };
  const reset = (root, suppress) => {
    const state = stateFor(root);
    clearTimeout(state.timer);
    state.timer = null;
    state.pinned = false;
    state.suppressed = suppress;
    triggerFor(root)?.setAttribute('aria-expanded', 'false');
    if (active === root) {
      active = null;
      cancelAnimationFrame(frame);
      frame = null;
    }
  };
  const close = (root = active, suppress = true, restoreFocus = false) => {
    if (!root) return;
    const panel = panelFor(root);
    if (suppress && active === root) {
      hoverDismissed = true;
      dismissedPosition = pointerPosition;
    }
    reset(root, suppress);
    if (panel?.matches(':popover-open')) panel.hidePopover();
    if (restoreFocus && root.isConnected && isReady(root)) triggerFor(root)?.focus({ preventScroll: true });
  };
  const position = (root) => {
    const panel = panelFor(root);
    const trigger = triggerFor(root).getBoundingClientRect();
    const style = getComputedStyle(panel);
    const inset = parseFloat(style.paddingLeft) || 0;
    const gap = parseFloat(style.rowGap) || 0;
    const viewport = window.visualViewport;
    const left = (viewport?.offsetLeft || 0) + inset;
    const top = (viewport?.offsetTop || 0) + inset;
    const right = left + (viewport?.width || window.innerWidth) - inset * 2;
    const bottom = top + (viewport?.height || window.innerHeight) - inset * 2;
    panel.style.maxWidth = `${Math.max(0, right - left)}px`;
    panel.style.maxHeight = `${Math.max(0, bottom - top)}px`;
    const rect = panel.getBoundingClientRect();
    const above = trigger.top - gap - rect.height;
    const below = trigger.bottom + gap;
    const preferred = above >= top ? above : below + rect.height <= bottom ? below : above;
    let center = trigger.left + trigger.width / 2;
    if (window.innerWidth < 750) {
      // Center across the visible shelf/grid, not an individual mobile column.
      // Prefer the rail viewport to its potentially transformed inner product grid.
      const container = root.closest('.arctic-rail__viewport') || root.closest('.product-grid');
      const containerRect = container?.getBoundingClientRect();
      center = containerRect && containerRect.width > 0
        ? containerRect.left + containerRect.width / 2
        : (left + right) / 2;
    }
    const centeredLeft = center - rect.width / 2;
    panel.style.left = `${Math.max(left, Math.min(centeredLeft, right - rect.width))}px`;
    panel.style.top = `${Math.max(top, Math.min(preferred, bottom - rect.height))}px`;
    return trigger;
  };
  const show = (root, pinned = false) => {
    if (!isReady(root)) return;
    const panel = panelFor(root);
    if (!panel?.showPopover) return;
    if (active && active !== root) close();
    const state = stateFor(root);
    clearTimeout(state.timer);
    state.pinned = pinned;
    state.suppressed = false;
    active = root;
    panel.showPopover({ source: triggerFor(root) });
    triggerFor(root).setAttribute('aria-expanded', 'true');
    const anchor = position(root);
    cancelAnimationFrame(frame);
    // Embla moves with transforms, so scrolling alone cannot detect a rail move.
    // While one panel is open, watch its anchor; never write card/rail dimensions.
    const watchAnchor = () => {
      if (active !== root) return;
      const rect = triggerFor(root).getBoundingClientRect();
      if (!isReady(root) || Math.abs(rect.left - anchor.left) > 1 || Math.abs(rect.top - anchor.top) > 1) {
        close(root);
        return;
      }
      frame = requestAnimationFrame(watchAnchor);
    };
    frame = requestAnimationFrame(watchAnchor);
  };

  document.addEventListener('pointermove', (event) => {
    pointerPosition = { x: event.clientX, y: event.clientY };
    if (hoverDismissed && (!dismissedPosition || pointerPosition.x !== dismissedPosition.x || pointerPosition.y !== dismissedPosition.y)) {
      hoverDismissed = false;
    }
  }, true);
  document.addEventListener('pointerover', (event) => {
    if (!hoverMedia.matches || event.pointerType === 'touch' || hoverDismissed) return;
    const card = cardFor(event.target);
    const root = card?.querySelector(selector);
    if (!root) return;
    const state = stateFor(root);
    clearTimeout(state.timer);
    if (card.contains(event.relatedTarget)) return;
    state.hovered = true;
    if (!state.suppressed && active !== root && !(active && stateFor(active).pinned)) show(root);
  });
  document.addEventListener('pointerout', (event) => {
    const card = cardFor(event.target);
    if (!card || card.contains(event.relatedTarget)) return;
    const root = card.querySelector(selector);
    if (!root) return;
    const state = stateFor(root);
    state.hovered = false;
    state.suppressed = false;
    clearTimeout(state.timer);
    // Allow crossing the small visual gap into the floating receipt.
    state.timer = setTimeout(() => {
      if (active === root && !state.hovered && !state.pinned && !root.contains(document.activeElement)) close(root, false);
    }, 180);
  });
  document.addEventListener('click', (event) => {
    const element = asElement(event.target);
    const trigger = element?.closest(triggerSelector);
    const root = trigger?.closest(selector);
    if (root) {
      event.preventDefault();
      const state = stateFor(root);
      if (!isReady(root)) return close(root);
      if (active === root) {
        if (event.detail > 0 && !state.pinned) state.pinned = true;
        else close(root);
      } else show(root, true);
      return;
    }
  });
  document.addEventListener('pointerdown', (event) => {
    if (active && !active.contains(event.target)) close();
  }, true);
  document.addEventListener('focusin', (event) => {
    if (active && !active.contains(event.target)) close();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !active) return;
    const restoreFocus = active.contains(document.activeElement);
    event.preventDefault();
    event.stopPropagation();
    escapeKeyup = true;
    close(active, true, restoreFocus);
  }, true);
  // Consume the matching keyup so Dawn's surrounding accordion stays open.
  document.addEventListener('keyup', (event) => {
    if (event.key !== 'Escape' || !escapeKeyup) return;
    escapeKeyup = false;
    event.preventDefault();
    event.stopPropagation();
  }, true);
  document.addEventListener('beforetoggle', (event) => {
    if (event.newState === 'closed' && asElement(event.target)?.matches(panelSelector)) {
      const root = event.target.closest(selector);
      if (active === root) {
        hoverDismissed = true;
        dismissedPosition = pointerPosition;
        reset(root, true);
      }
    }
  }, true);
  document.addEventListener('scroll', (event) => {
    if (active && !panelFor(active).contains(event.target)) close();
  }, true);
  document.addEventListener('arctic-header:overlay:open', () => close());
  window.addEventListener('resize', () => close());
  window.visualViewport?.addEventListener('resize', () => close());
  window.visualViewport?.addEventListener('scroll', () => close());
  hoverMedia.addEventListener('change', () => close());

  const init = () => {
    const observer = new MutationObserver(() => {
      if (!active || isReady(active)) return;
      const card = cardFor(active);
      const focused = active.contains(document.activeElement);
      close();
      if (focused && card?.isConnected) card.querySelector('.card__heading a')?.focus({ preventScroll: true });
    });
    observer.observe(document.body, {
      subtree: true, childList: true, characterData: true, attributes: true,
      attributeFilter: ['data-northbound-shipping-cents', 'data-northbound-grand-total-cents'],
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
